import React from 'react';
import styled, { css } from 'styled-components';
import { Icon, Text } from 'components';
import { theme, toRem } from 'design';
import { CardType } from 'types';
import { getPaymentTypeIcon } from 'utils';

export const ContentContainer = styled.section`
  justify-self: center;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;

  button {
    width: auto;
    max-width: fit-content;
    padding: 0 ${toRem(22)};
  }
`;

interface ContentProps {
  splitLayout?: boolean;
}

const ApplePayIcon = styled(Icon)`
  width: ${toRem(32)};
  height: ${toRem(20)};
`;

export const Content = styled.section<ContentProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)} ${toRem(16.5)};
  width: 100%;
  padding: ${toRem(22)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[100]};
  text-align: left;

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(33)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    ${({ splitLayout }) =>
      splitLayout &&
      css`
        grid-template-columns: 1fr auto;
        grid-gap: ${toRem(22)};
        align-items: center;
      `}
  }
`;

export const Header = styled.header`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  margin-bottom: ${toRem(22)};
`;

export const PaymentMethodDisclaimer = styled.li`
  display: flex;
  width: 100%;
  padding: ${toRem(22)} 0;

  ${Text} {
    width: 100%;
  }
`;

export const PaymentMethodActions = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-top: ${toRem(16.5)};

  button {
    width: auto;
    max-width: fit-content;
  }

  > * {
    &:first-child {
      margin-right: ${toRem(11)};
    }
  }
`;

interface IPaymentMethodAccountNumberProps {
  align?: 'start' | 'end';
  cardType?: CardType | undefined;
  cardNumber?: string;
  hideRedacted?: boolean;
  isApplePay?: boolean;
  isProcessing?: boolean;
  short?: boolean;
  fontSize?: 'default' | 'small';
  fontWeight?: 'medium' | 'regular';
}

const PMAccountNumberLayout = styled.div<Partial<IPaymentMethodAccountNumberProps>>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;

  ${({ align }) =>
    align === 'start' &&
    css`
      justify-content: flex-start;
    `}
`;

const PMIcon = styled(Icon)`
  margin-right: ${toRem(5.5)};
`;

export const PaymentMethodAccountNumber = ({ align, cardType, cardNumber, hideRedacted = false, isApplePay = false, isProcessing, short = false, fontSize = 'default', fontWeight = 'medium' }: IPaymentMethodAccountNumberProps) => {
  const PaymentMethodIcon = getPaymentTypeIcon(cardType);
  const cardNumberDisplay =
    cardType === CardType.AMERICAN_EXPRESS.toLowerCase() ? (
      <>
        {!short && !hideRedacted && '•••• •••••• •'}
        {short && !hideRedacted && ' •••• '}
        {cardNumber}
      </>
    ) : (
      <>
        {!short && !hideRedacted && '•••• ••••'}
        {!hideRedacted && ' •••• '}
        {cardNumber}
      </>
    );

  const renderRightContent = () => {
    if (isProcessing) return <>•••• •••• •••• ••••</>;

    if (isApplePay) return <ApplePayIcon icon="ApplePay" dataCy="apple-pay-mark" />;

    return (
      <Text size={fontSize} fontWeight={fontWeight}>
        {cardNumber ? cardNumberDisplay : '--'}
      </Text>
    );
  };

  return (
    <PMAccountNumberLayout align={align}>
      {cardNumber && cardType && !isProcessing && <PMIcon label={`Payment Method Type: ${cardType}`} icon={PaymentMethodIcon} width={32} height={20} />}

      {renderRightContent()}
    </PMAccountNumberLayout>
  );
};

// ASV = Account Setup View
export const ASVLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.layout.gutter.small)};
  justify-items: center;
  width: 100%;

  > img {
    width: 100%;
    max-width: ${toRem(300)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(44)};

    > img {
      max-width: ${toRem(350)};
    }
  }
`;
